import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Users, Package, TrendingUp, AlertTriangle, Eye, Ban, CheckCircle, XCircle, Edit, Trash2, Plus, Search, Shield, RefreshCw, Mail, Loader2 } from 'lucide-react'
import { toast } from 'react-toastify'
import apiService from '../../services/api'
import { visitCounterService } from '../../services/visitCounterService'
import type { User, Product, Category, Order, ApiResponse } from '../../types'
import { Dialog } from '@headlessui/react'

// Local UserRole enum to avoid import issues
enum UserRole {
  BUYER = 'BUYER',
  VENDOR = 'VENDOR',
  ADMIN = 'ADMIN',
  MODERATOR = 'MODERATOR' // Keep for future use, but not actively used
}

type TabType = 'overview' | 'users' | 'products' | 'categories' | 'orders' | 'pendingGovCerts' | 'govAgencies'

interface AdminStats {
  totalUsers: number
  totalProducts: number
  totalOrders: number
  totalRevenue: number
  pendingProducts: number
  reportedProducts: number
  activeAuctions: number
  newUsersToday: number
}

export default function AdminDashboardPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [activeTab, setActiveTab] = useState<TabType>((location.state as any)?.activeTab || 'overview')
  const [loading, setLoading] = useState(true)
  
  // Data states
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    totalProducts: 0,
    totalOrders: 0,
    totalRevenue: 0,
    pendingProducts: 0,
    reportedProducts: 0,
    activeAuctions: 0,
    newUsersToday: 0
  })
  const [users, setUsers] = useState<User[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [orders, setOrders] = useState<Order[]>([])
  
  // Search and filter states
  const [userSearch, setUserSearch] = useState('')
  const [productSearch, setProductSearch] = useState('')
  const [orderSearch, setOrderSearch] = useState('')
  const [selectedStatus, setSelectedStatus] = useState<string>('')
  const [selectedUserStatus, setSelectedUserStatus] = useState<string>('')
  const [selectedOrderStatus, setSelectedOrderStatus] = useState<string>('')
  const [selectedUserType, setSelectedUserType] = useState<string>('')
  const [selectedUserRole, setSelectedUserRole] = useState<string>('')

  // Gov Agencies state
  const [govAgencies, setGovAgencies] = useState<any[]>([])
  const [govAgenciesStats, setGovAgenciesStats] = useState({ total: 0, pending: 0, approved: 0 })
  const [govAgenciesLoading, setGovAgenciesLoading] = useState(false)
  const [govAgenciesFilter, setGovAgenciesFilter] = useState<'all' | 'pending' | 'approved'>('pending')
  const [selectedGovAgency, setSelectedGovAgency] = useState<any>(null)
  const [showGovRejectModal, setShowGovRejectModal] = useState(false)
  const [govRejectReason, setGovRejectReason] = useState('')
  const [govProcessingId, setGovProcessingId] = useState<string | null>(null)
  
  // Email sending loading state
  const [sendingEmails, setSendingEmails] = useState<Set<string>>(new Set())

  // Visit counter state
  const [visitStats, setVisitStats] = useState({ total: 0, today: 0 })

  const modalCloseRef = useRef(null)

  useEffect(() => {
    fetchAdminData()
    // Track dashboard visit
    trackDashboardVisit()
  }, [])

  const trackDashboardVisit = async () => {
    try {
      const stats = await visitCounterService.trackAndGetStats()
      setVisitStats(stats)
    } catch (error) {
      console.error('Failed to track dashboard visit:', error)
    }
  }

  // Fetch gov agencies data when govAgencies tab becomes active
  useEffect(() => {
    if (activeTab === 'govAgencies' && govAgencies.length === 0) {
      fetchGovAgencies()
    }
  }, [activeTab, govAgencies.length])

  const fetchAdminData = async () => {
    setLoading(true)
    try {
      const [statsRes, usersRes, productsRes, categoriesRes, ordersRes] = await Promise.all([
        apiService.get<ApiResponse<AdminStats>>('/api/admin/stats'),
        apiService.get<ApiResponse<User[]>>('/api/admin/users'),
        apiService.get<ApiResponse<Product[]>>('/api/admin/products'),
        apiService.get<ApiResponse<Category[]>>('/api/admin/categories'),
        apiService.get<ApiResponse<Order[]>>('/api/admin/orders')
      ])

      setStats(statsRes.data || stats)
      setUsers(usersRes.data || [])
      setProducts(productsRes.data || [])
      setCategories(categoriesRes.data || [])
      setOrders(ordersRes.data || [])
    } catch (error) {
      console.error('Failed to fetch admin data:=======>', error)
      toast.error('Failed to load admin data')
    } finally {
      setLoading(false)
    }
  }

  const fetchGovAgencies = async () => {
    setGovAgenciesLoading(true)
    try {
      const response = await apiService.get('/api/admin/gov-agencies') as any
      if (response.success) {
        setGovAgencies(response.data || [])
        // Calculate stats
        const total = response.data?.length || 0
        const pending = response.data?.filter((agency: any) => !agency.approved).length || 0
        const approved = response.data?.filter((agency: any) => agency.approved).length || 0
        setGovAgenciesStats({ total, pending, approved })
      }
    } catch (error) {
      console.error('Failed to fetch gov agencies:', error)
      toast.error('Failed to load government agencies')
    } finally {
      setGovAgenciesLoading(false)
    }
  }

  const handleGovAgencyAction = async (agencyId: string, action: 'approve' | 'reject') => {
    setGovProcessingId(agencyId)
    try {
      if (action === 'approve') {
        await apiService.post(`/api/admin/gov-agencies/${agencyId}/approve`)
        toast.success('Government agency approved successfully')
      } else {
        if (!govRejectReason.trim()) {
          toast.error('Please provide a reason for rejection')
          return
        }
        await apiService.post(`/api/admin/gov-agencies/${agencyId}/reject`, {
          reason: govRejectReason
        })
        toast.success('Government agency rejected')
        setShowGovRejectModal(false)
        setGovRejectReason('')
      }
      await fetchGovAgencies()
    } catch (error: any) {
      console.error(`Failed to ${action} gov agency:`, error)
      toast.error(error.response?.data?.message || `Failed to ${action} agency`)
    } finally {
      setGovProcessingId(null)
    }
  }

  const handleUserAction = async (userId: string, action: 'ban' | 'unban' | 'verify') => {
    try {
      await apiService.post(`/api/admin/users/${userId}/${action}`)
      toast.success(`User ${action}ned successfully`)
      fetchAdminData()
    } catch (error: any) {
      toast.error(error.response?.data?.message || `Failed to ${action} user`)
    }
  }

  const handleSendVerificationEmail = async (userId: string) => {
    // Add user to loading set
    setSendingEmails(prev => new Set(prev).add(userId))
    
    try {
      await apiService.post(`/api/admin/users/${userId}/send-verification-email`)
      toast.success('Verification email sent successfully')
      fetchAdminData()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send verification email')
    } finally {
      // Remove user from loading set
      setSendingEmails(prev => {
        const newSet = new Set(prev)
        newSet.delete(userId)
        return newSet
      })
    }
  }

  const handleProductAction = async (productId: string, action: 'approve' | 'reject' | 'suspend') => {
    try {
      await apiService.post(`/api/admin/products/${productId}/${action}`)
      toast.success(`Product ${action}ed successfully`)
      fetchAdminData()
    } catch (error: any) {
      toast.error(error.response?.data?.message || `Failed to ${action} product`)
    }
  }

  const handleDeleteCategory = async (categoryId: string) => {
    if (!confirm('Are you sure you want to delete this category?')) return

    try {
      await apiService.delete(`/api/admin/categories/${categoryId}`)
      toast.success('Category deleted successfully')
      fetchAdminData()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete category')
    }
  }



  const UserRow = ({ user }: { user: User }) => {
    // Determine user type for display (5 main roles: Regular Buyer, Regular Vendor, Enterprise Buyer, Enterprise Vendor, Admin)
    const getUserType = () => {
      if (user.role === UserRole.ADMIN) return 'Admin'
      if (user.role === UserRole.MODERATOR) return 'Moderator' // Future use
      if (user.role === UserRole.VENDOR && user.isEnterprise && user.isGovCertified) return 'Gov Certified Vendor'
      if (user.role === UserRole.VENDOR && user.isEnterprise) return 'Enterprise Vendor'
      if (user.role === UserRole.VENDOR && !user.isEnterprise) return 'Regular Vendor'
      if (user.role === UserRole.BUYER && user.isEnterprise) return 'Enterprise Buyer'
      if (user.role === UserRole.BUYER && !user.isEnterprise) return 'Regular Buyer'
      return 'User'
    }

    const getUserTypeColor = () => {
      if (user.role === UserRole.ADMIN) return 'bg-purple-100 text-purple-800'
      if (user.role === UserRole.MODERATOR) return 'bg-blue-100 text-blue-800' // Future use
      if (user.role === UserRole.VENDOR && user.isEnterprise && user.isGovCertified) return 'bg-emerald-100 text-emerald-800'
      if (user.role === UserRole.VENDOR && user.isEnterprise) return 'bg-orange-100 text-orange-800'
      if (user.role === UserRole.VENDOR && !user.isEnterprise) return 'bg-green-100 text-green-800'
      if (user.role === UserRole.BUYER && user.isEnterprise) return 'bg-yellow-100 text-yellow-800'
      if (user.role === UserRole.BUYER && !user.isEnterprise) return 'bg-blue-100 text-blue-800'
      return 'bg-gray-100 text-gray-800'
    }

    return (
      <tr className="hover:bg-gray-50">
        <td className="px-3 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
              {user.avatar ? (
                <img src={user.avatar} alt={user.firstName} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover" />
              ) : (
                <span className="text-xs sm:text-sm font-medium text-gray-600">
                  {user.firstName?.[0] || user.email[0].toUpperCase()}
                </span>
              )}
            </div>
            <div className="ml-3 sm:ml-4 flex-1 min-w-0">
              <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">
                {user.firstName} {user.lastName}
                {user.isGovCertified && (
                  <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 ml-1 sm:ml-2">
                    🏛️ Gov Certified
                  </span>
                )}
                {user.banned && (
                  <span className="inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 ml-1 sm:ml-2">
                    Banned
                  </span>
                )}
                {user.companyName && (
                  <span className="text-xs text-gray-500 block truncate">
                    {user.companyName}
                    {user.cageCode && (
                      <span className="text-xs text-blue-600 ml-1 sm:ml-2">CAGE: {user.cageCode}</span>
                    )}
                  </span>
                )}
              </div>
              <div className="text-xs sm:text-sm text-gray-500 truncate">
                {user.email}
                {user.dunsNumber && (
                  <span className="text-xs text-gray-400 block">DUNS: {user.dunsNumber}</span>
                )}
              </div>
            </div>
          </div>
        </td>
        <td className="px-3 sm:px-6 py-3 sm:py-4 hidden sm:table-cell">
          <span className={`px-1.5 sm:px-2 py-1 text-xs rounded ${getUserTypeColor()}`}>
            {getUserType()}
          </span>
        </td>
        <td className="px-3 sm:px-6 py-3 sm:py-4 hidden md:table-cell">
          <span className={`px-1.5 sm:px-2 py-1 text-xs rounded ${
            user.verified && user.emailSent 
              ? 'bg-green-100 text-green-800' 
              : user.verified 
                ? 'bg-blue-100 text-blue-800' 
                : 'bg-yellow-100 text-yellow-800'
          }`}>
            {user.verified && user.emailSent 
              ? 'Email Sent' 
              : user.verified 
                ? 'Approved' 
                : 'Pending'}
          </span>
        </td>
        <td className="px-3 sm:px-6 py-3 sm:py-4 hidden lg:table-cell text-xs sm:text-sm text-gray-500">
          {new Date(user.createdAt).toLocaleDateString()}
        </td>
        <td className="px-3 sm:px-6 py-3 sm:py-4 text-right">
          <div className="flex justify-end space-x-1 sm:space-x-2">
            {!user.verified && (
              <button
                onClick={() => handleUserAction(user.id, 'verify')}
                className="text-green-600 hover:text-green-900 p-1"
                title="Approve User"
              >
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            )}
            {user.verified && !user.emailSent && (
              <button
                onClick={() => handleSendVerificationEmail(user.id)}
                disabled={sendingEmails.has(user.id)}
                className={`p-1 ${sendingEmails.has(user.id) 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-blue-600 hover:text-blue-900'
                }`}
                title={sendingEmails.has(user.id) ? "Sending email..." : "Send Verification Email"}
              >
                {sendingEmails.has(user.id) ? (
                  <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                ) : (
                  <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                )}
              </button>
            )}
            {user.verified && user.emailSent && (
              <button
                onClick={() => handleSendVerificationEmail(user.id)}
                disabled={sendingEmails.has(user.id)}
                className={`p-1 ${sendingEmails.has(user.id) 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-gray-600 hover:text-gray-900'
                }`}
                title={sendingEmails.has(user.id) ? "Sending email..." : "Resend Verification Email"}
              >
                {sendingEmails.has(user.id) ? (
                  <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                ) : (
                  <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                )}
              </button>
            )}
            <button
              onClick={() => handleUserAction(user.id, user.banned ? 'unban' : 'ban')}
              className={`p-1 ${user.banned ? 'text-green-600 hover:text-green-900' : 'text-red-600 hover:text-red-900'}`}
              title={user.banned ? 'Unban User' : 'Ban User'}
            >
              <Ban className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
          </div>
        </td>
      </tr>
    )
  }

  const ProductRow = ({ product }: { product: Product }) => (
    <tr className="hover:bg-gray-50">
      <td className="px-3 sm:px-6 py-3 sm:py-4">
        <div className="flex items-center">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-200 rounded overflow-hidden flex-shrink-0">
            {product.imageUrls?.[0] ? (
              <img src={product.imageUrls[0]} alt={product.title} className="w-10 h-10 sm:w-12 sm:h-12 object-cover" />
            ) : (
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
              </div>
            )}
          </div>
          <div className="ml-3 sm:ml-4 flex-1 min-w-0">
            <div className="text-xs sm:text-sm font-medium text-gray-900 truncate">
              {product.title}
            </div>
            <div className="text-xs sm:text-sm text-gray-500 truncate">{product.category?.name}</div>
          </div>
        </div>
      </td>
      <td className="px-3 sm:px-6 py-3 sm:py-4 hidden sm:table-cell">
        <div className="text-xs sm:text-sm text-gray-900 truncate">
          {product.owner?.firstName} {product.owner?.lastName}
        </div>
        <div className="text-xs sm:text-sm text-gray-500 truncate">{product.owner?.email}</div>
      </td>
      <td className="px-3 sm:px-6 py-3 sm:py-4 hidden md:table-cell">
        <span className="text-xs sm:text-sm font-medium text-green-600">
          ${product.isAuction ? (product.currentBid || product.minimumBid || 0) : product.price}
        </span>
      </td>
      <td className="px-3 sm:px-6 py-3 sm:py-4 hidden lg:table-cell">
        <span className={`px-1.5 sm:px-2 py-1 text-xs rounded ${
          product.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
          product.status === 'DRAFT' ? 'bg-yellow-100 text-yellow-800' :
          product.status === 'SUSPENDED' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800'
        }`}>
          {product.status}
        </span>
      </td>
      <td className="px-3 sm:px-6 py-3 sm:py-4 hidden xl:table-cell text-xs sm:text-sm text-gray-500">
        {new Date(product.createdAt).toLocaleDateString()}
      </td>
      <td className="px-3 sm:px-6 py-3 sm:py-4 text-right">
        <div className="flex justify-end space-x-1 sm:space-x-2">
          <button
            onClick={() => navigate(`/products/${product.id}`)}
            className="text-blue-600 hover:text-blue-900 p-1"
            title="View Product"
          >
            <Eye className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
          {product.status === 'DRAFT' && (
            <>
              <button
                onClick={() => handleProductAction(product.id, 'approve')}
                className="text-green-600 hover:text-green-900 p-1"
                title="Approve Product"
              >
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
              <button
                onClick={() => handleProductAction(product.id, 'reject')}
                className="text-red-600 hover:text-red-900 p-1"
                title="Reject Product"
              >
                <XCircle className="w-3 h-3 sm:w-4 sm:h-4" />
              </button>
            </>
          )}
          {product.status === 'ACTIVE' && (
            <button
              onClick={() => handleProductAction(product.id, 'suspend')}
              className="text-orange-600 hover:text-orange-900 p-1"
              title="Suspend Product"
            >
              <Ban className="w-3 h-3 sm:w-4 sm:h-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  )

  const CategoryRow = ({ category }: { category: Category }) => (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          {category.imageUrl && (
            <img src={category.imageUrl} alt={category.name} className="w-10 h-10 rounded object-cover mr-3" />
          )}
          <div>
            <div className="text-sm font-medium text-gray-900">{category.name}</div>
            {category.description && (
              <div className="text-sm text-gray-500 truncate max-w-xs">{category.description}</div>
            )}
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {category.parent?.name || 'Root Category'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {category.children?.length || 0} subcategories
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {new Date(category.createdAt).toLocaleDateString()}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/admin/categories/${category.id}/edit`)}
            className="text-blue-600 hover:text-blue-900"
            title="Edit Category"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDeleteCategory(category.id)}
            className="text-red-600 hover:text-red-900"
            title="Delete Category"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  )

  const OverviewTab = () => (
    <div className="space-y-4">
      {/* Stats Grid - Mobile First */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-6">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Total Users</p>
              <p className="text-lg font-bold text-gray-900">{stats.totalUsers}</p>
              <p className="text-xs text-green-600">+{stats.newUsersToday} today</p>
            </div>
            <div className="bg-blue-500 p-2 rounded-full">
              <Users className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Total Products</p>
              <p className="text-lg font-bold text-gray-900">{stats.totalProducts}</p>
            </div>
            <div className="bg-green-500 p-2 rounded-full">
              <Package className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Total Orders</p>
              <p className="text-lg font-bold text-gray-900">{stats.totalOrders}</p>
            </div>
            <div className="bg-purple-500 p-2 rounded-full">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Revenue</p>
              <p className="text-lg font-bold text-gray-900">${stats.totalRevenue.toLocaleString()}</p>
            </div>
            <div className="bg-orange-500 p-2 rounded-full">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-600">Dashboard Visits</p>
              <p className="text-lg font-bold text-gray-900">{visitStats.total.toLocaleString()}</p>
              <p className="text-xs text-blue-600">{visitStats.today} today</p>
            </div>
            <div className="bg-indigo-500 p-2 rounded-full">
              <Eye className="w-4 h-4 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Alert Cards - Mobile First */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1 min-w-0">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 mr-2 sm:mr-3 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="text-xs sm:text-sm font-medium text-yellow-800 truncate">Pending Products</h3>
                <p className="text-xs text-yellow-700 truncate">Products awaiting approval</p>
              </div>
            </div>
            <div className="text-lg sm:text-2xl font-bold text-yellow-900 ml-2 flex-shrink-0">{stats.pendingProducts}</div>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1 min-w-0">
              <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-2 sm:mr-3 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="text-xs sm:text-sm font-medium text-red-800 truncate">Reported Products</h3>
                <p className="text-xs text-red-700 truncate">Products with reports</p>
              </div>
            </div>
            <div className="text-lg sm:text-2xl font-bold text-red-900 ml-2 flex-shrink-0">{stats.reportedProducts}</div>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center flex-1 min-w-0">
              <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2 sm:mr-3 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="text-xs sm:text-sm font-medium text-blue-800 truncate">Active Auctions</h3>
                <p className="text-xs text-blue-700 truncate">Currently running auctions</p>
              </div>
            </div>
            <div className="text-lg sm:text-2xl font-bold text-blue-900 ml-2 flex-shrink-0">{stats.activeAuctions}</div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
          <h2 className="text-xl font-semibold">Quick Actions</h2>
          <button
            onClick={fetchAdminData}
            disabled={loading}
            className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 text-sm sm:text-base"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh Data</span>
            <span className="sm:hidden">Refresh</span>
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          <button
            onClick={() => setActiveTab('users')}
            className="btn btn-outline text-sm sm:text-base py-2 px-3 sm:px-4"
          >
            <Users className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Manage Users</span>
            <span className="sm:hidden">Users</span>
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className="btn btn-outline text-sm sm:text-base py-2 px-3 sm:px-4"
          >
            <Package className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Review Products</span>
            <span className="sm:hidden">Products</span>
          </button>
          <button
            onClick={() => navigate('/admin/gov-vendors')}
            className="btn btn-outline text-emerald-600 border-emerald-300 hover:bg-emerald-50 text-sm sm:text-base py-2 px-3 sm:px-4"
          >
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Gov Vendors</span>
            <span className="sm:hidden">Gov</span>
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className="btn btn-outline text-sm sm:text-base py-2 px-3 sm:px-4"
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Manage Categories</span>
            <span className="sm:hidden">Categories</span>
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className="btn btn-outline text-sm sm:text-base py-2 px-3 sm:px-4"
          >
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">View Orders</span>
            <span className="sm:hidden">Orders</span>
          </button>
        </div>
      </div>
    </div>
  )

  const UsersTab = () => {
    const filteredUsers = users.filter(user => {
      const matchesSearch = user.firstName?.toLowerCase().includes(userSearch.toLowerCase()) ||
                           user.lastName?.toLowerCase().includes(userSearch.toLowerCase()) ||
                           user.email.toLowerCase().includes(userSearch.toLowerCase())
      
      let matchesStatus = true
      let matchesType = true
      let matchesRole = true
      
      // REFACTORED: Standardized Enterprise User Detection
      // Using consistent logic: role + isEnterprise flag instead of ENTERPRISE_BUYER enum
        switch (selectedUserStatus) {
          case 'verified':
            matchesStatus = user.verified ?? false
            break
          case 'unverified':
          matchesStatus = !(user.verified ?? false)
            break
          case 'ADMIN':
          matchesStatus = user.role === UserRole.ADMIN
            break
          case 'REGULAR_BUYER':
            matchesStatus = user.role === UserRole.BUYER && !(user.isEnterprise ?? false)
            break
          case 'REGULAR_VENDOR':
            matchesStatus = user.role === UserRole.VENDOR && !(user.isEnterprise ?? false)
            break
          case 'ENTERPRISE_BUYER':
          // Uses standardized logic: role=BUYER + isEnterprise=true
            matchesStatus = user.role === UserRole.BUYER && (user.isEnterprise ?? false)
            break
          case 'ENTERPRISE_VENDOR':
          // Uses standardized logic: role=VENDOR + isEnterprise=true
            matchesStatus = user.role === UserRole.VENDOR && (user.isEnterprise ?? false)
            break
          case 'ALL_BUYERS':
            matchesStatus = user.role === UserRole.BUYER
            break
          case 'ALL_VENDORS':
            matchesStatus = user.role === UserRole.VENDOR
            break
          case 'GOV_CERTIFIED_VENDORS':
            matchesStatus = user.role === UserRole.VENDOR && (user.isGovCertified ?? false)
            break
          case 'BANNED':
            matchesStatus = user.banned ?? false
            break
          default:
            matchesStatus = true
      }

      // New filtering logic for user type
      if (selectedUserType) {
        matchesType = selectedUserType === 'Enterprise' ? (user.isEnterprise ?? false) : !(user.isEnterprise ?? false)
      }

      // New filtering logic for user role
      if (selectedUserRole) {
        matchesRole = selectedUserRole === 'Buyer' ? user.role === UserRole.BUYER : user.role === UserRole.VENDOR
      }

      return matchesSearch && matchesStatus && matchesType && matchesRole
    })

    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold">User Management</h2>
          
          {/* Filters - Mobile Responsive */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <select
              value={selectedUserType}
              onChange={(e) => setSelectedUserType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">All Types</option>
              <option value="Regular">Regular</option>
              <option value="Enterprise">Enterprise</option>
            </select>
            <select
              value={selectedUserRole}
              onChange={(e) => setSelectedUserRole(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">All Roles</option>
              <option value="Buyer">Buyer</option>
              <option value="Vendor">Vendor</option>
            </select>
            <select
              value={selectedUserStatus}
              onChange={(e) => setSelectedUserStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">All Users</option>
              <option value="REGULAR_BUYER">Regular Buyers</option>
              {/* REFACTORED: ENTERPRISE_BUYER label retained for admin UI consistency */}
              {/* Filtering logic uses standardized role=BUYER + isEnterprise=true */}
              <option value="ENTERPRISE_BUYER">Enterprise Buyers</option>
              <option value="REGULAR_VENDOR">Regular Vendors</option>
              {/* REFACTORED: ENTERPRISE_VENDOR label retained for admin UI consistency */}
              {/* Filtering logic uses standardized role=VENDOR + isEnterprise=true */}
              <option value="ENTERPRISE_VENDOR">Enterprise Vendors</option>
              <option value="ALL_BUYERS">All Buyers</option>
              <option value="ALL_VENDORS">All Vendors</option>
              <option value="GOV_CERTIFIED_VENDORS">Gov Certified Vendors</option>
              <option value="ADMIN">Admins</option>
              <option value="BANNED">Banned Users</option>
              <option value="verified">Verified Only</option>
              <option value="unverified">Unverified Only</option>
            </select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Type
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Joined
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <UserRow key={user.id} user={user} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  const ProductsTab = () => {
    const filteredProducts = products.filter(product => {
      const matchesSearch = product.title.toLowerCase().includes(productSearch.toLowerCase()) ||
                           product.owner?.email.toLowerCase().includes(productSearch.toLowerCase())
      const matchesStatus = !selectedStatus || product.status === selectedStatus
      return matchesSearch && matchesStatus
    })

    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold">Product Management</h2>
          
          {/* Filters - Mobile Responsive */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">All Status</option>
              <option value="DRAFT">Draft</option>
              <option value="ACTIVE">Active</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="SOLD">Sold</option>
            </select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Search products..."
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Owner
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Price
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden xl:table-cell">
                    Created
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredProducts.map((product) => (
                  <ProductRow key={product.id} product={product} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  const CategoriesTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Category Management</h2>
        <button
          onClick={() => navigate('/admin/categories/create')}
          className="btn btn-primary"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Category
        </button>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subcategories
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {categories.map((category) => (
                <CategoryRow key={category.id} category={category} />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const OrdersTab = () => {
    const filteredOrders = orders.filter(order => {
      const matchesSearch = order.product?.title.toLowerCase().includes(orderSearch.toLowerCase()) ||
                           order.buyer?.firstName?.toLowerCase().includes(orderSearch.toLowerCase()) ||
                           order.buyer?.lastName?.toLowerCase().includes(orderSearch.toLowerCase()) ||
                           order.buyer?.email.toLowerCase().includes(orderSearch.toLowerCase()) ||
                           order.seller?.firstName?.toLowerCase().includes(orderSearch.toLowerCase()) ||
                           order.seller?.lastName?.toLowerCase().includes(orderSearch.toLowerCase()) ||
                           order.seller?.email.toLowerCase().includes(orderSearch.toLowerCase()) ||
                           order.id.toLowerCase().includes(orderSearch.toLowerCase())
      
      const matchesStatus = !selectedOrderStatus || order.status === selectedOrderStatus
      
      return matchesSearch && matchesStatus
    })

    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold">Order Management</h2>
          
          {/* Filters - Mobile Responsive */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select
              value={selectedOrderStatus}
              onChange={(e) => setSelectedOrderStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="">All Orders</option>
              <option value="PENDING">Pending</option>
              <option value="CONFIRMED">Confirmed</option>
              <option value="SHIPPED">Shipped</option>
              <option value="DELIVERED">Delivered</option>
              <option value="CANCELLED">Cancelled</option>
              <option value="REFUNDED">Refunded</option>
            </select>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                placeholder="Search orders..."
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Order ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Buyer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Seller
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{order.id.slice(-8)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.product?.title}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.buyer?.firstName} {order.buyer?.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {order.seller?.firstName} {order.seller?.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                      ${order.totalAmount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded ${
                        order.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                        order.status === 'CONFIRMED' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'SHIPPED' ? 'bg-purple-100 text-purple-800' :
                        order.status === 'DELIVERED' ? 'bg-green-100 text-green-800' :
                        order.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                        order.status === 'REFUNDED' ? 'bg-orange-100 text-orange-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    )
  }

  const PendingGovCertificationsTab = () => {
    const [pendingVendors, setPendingVendors] = useState<User[]>([])
    const [loading, setLoading] = useState(true)
    const [selectedVendor, setSelectedVendor] = useState<User | null>(null)
    const [showModal, setShowModal] = useState(false)
    const [rejectReason, setRejectReason] = useState('')

    useEffect(() => {
      const fetchPendingVendors = async () => {
        setLoading(true)
        try {
          const res = await apiService.get<ApiResponse<User[]>>('/api/admin/gov-vendors?status=pending')
          setPendingVendors(res.data || [])
        } catch (e) {
          toast.error('Failed to load pending vendors')
        } finally {
          setLoading(false)
        }
      }
      fetchPendingVendors()
    }, [])

    const handleApprove = async (vendor: User) => {
      try {
        await apiService.post(`/api/admin/gov-vendors/${vendor.id}/approve`, { notes: 'Approved via admin dashboard' })
        toast.success('Vendor approved')
        setShowModal(false)
        setPendingVendors(pendingVendors.filter(v => v.id !== vendor.id))
      } catch (e) {
        toast.error('Approval failed')
      }
    }
    const handleReject = async (vendor: User) => {
      try {
        await apiService.post(`/api/admin/gov-vendors/${vendor.id}/reject`, { reason: rejectReason || 'Rejected via admin dashboard', notes: rejectReason })
        toast.success('Vendor rejected')
        setShowModal(false)
        setPendingVendors(pendingVendors.filter(v => v.id !== vendor.id))
      } catch (e) {
        toast.error('Rejection failed')
      }
    }
    return (
      <div className="space-y-6">
        <h2 className="text-2xl font-semibold">Pending Government Certifications</h2>
        {loading ? <div>Loading...</div> : (
                  <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CAGE</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DUNS</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Certifications</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {pendingVendors.map(vendor => (
                  <tr key={vendor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">{vendor.firstName} {vendor.lastName} <br /><span className="text-xs text-gray-500">{vendor.email}</span></td>
                    <td className="px-6 py-4 whitespace-nowrap">{vendor.companyName}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{vendor.cageCode}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{vendor.dunsNumber}</td>
                    <td className="px-6 py-4 whitespace-nowrap">{(vendor.govCertifications || []).join(', ')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button className="btn btn-sm btn-primary mr-2" onClick={() => { setSelectedVendor(vendor); setShowModal(true); }}>Review</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </div>
        )}
        {/* Modal for review/approve/reject */}
        <Dialog open={showModal} onClose={() => setShowModal(false)} initialFocus={modalCloseRef} className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div className="fixed inset-0 bg-black opacity-30" />
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 z-20">
              <h2 className="text-lg font-bold mb-2">Review Vendor Certification</h2>
              {selectedVendor && (
                <div className="space-y-2">
                  <div><b>Name:</b> {selectedVendor.firstName} {selectedVendor.lastName}</div>
                  <div><b>Email:</b> {selectedVendor.email}</div>
                  <div><b>Company:</b> {selectedVendor.companyName}</div>
                  <div><b>CAGE:</b> {selectedVendor.cageCode}</div>
                  <div><b>DUNS:</b> {selectedVendor.dunsNumber}</div>
                  <div><b>Certifications:</b> {(selectedVendor.govCertifications || []).join(', ')}</div>
                  <div><b>Submitted At:</b> {new Date(selectedVendor.updatedAt).toLocaleString()}</div>
                  <div className="flex space-x-2 mt-4">
                    <button className="btn btn-success" onClick={() => handleApprove(selectedVendor)}>Approve</button>
                    <button className="btn btn-danger" onClick={() => handleReject(selectedVendor)}>Reject</button>
                    <button className="btn btn-secondary" ref={modalCloseRef} onClick={() => setShowModal(false)}>Close</button>
                  </div>
                  <div className="mt-2">
                    <label className="block text-xs text-gray-500">Rejection Reason (optional):</label>
                    <textarea 
                      className="w-full border rounded p-2 mt-1" 
                      value={rejectReason} 
                      onChange={e => setRejectReason(e.target.value)}
                      rows={3}
                      placeholder="Enter reason for rejection..."
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </Dialog>
      </div>
    )
  }

  const GovAgenciesTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Government Agency Approvals</h2>
          <p className="text-gray-600">Review and approve government agency applications</p>
        </div>
        <button
          onClick={fetchGovAgencies}
          className="btn btn-outline"
          disabled={govAgenciesLoading}
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${govAgenciesLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Agencies</p>
              <p className="text-2xl font-bold text-gray-900">{govAgenciesStats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Pending Review</p>
              <p className="text-2xl font-bold text-gray-900">{govAgenciesStats.pending}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Approved</p>
              <p className="text-2xl font-bold text-gray-900">{govAgenciesStats.approved}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Filter:</span>
          <div className="flex space-x-2">
            {(['all', 'pending', 'approved'] as const).map((filter) => (
              <button
                key={filter}
                onClick={() => setGovAgenciesFilter(filter)}
                className={`px-3 py-1 text-sm rounded-md ${
                  govAgenciesFilter === filter
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {filter.charAt(0).toUpperCase() + filter.slice(1)} ({filter === 'all' ? govAgenciesStats.total : filter === 'pending' ? govAgenciesStats.pending : govAgenciesStats.approved})
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Agencies List */}
      {govAgenciesLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading government agencies...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h3 className="text-lg font-medium text-gray-900">Agency Applications</h3>
          </div>

          {govAgencies.filter(agency =>
            govAgenciesFilter === 'all' ||
            (govAgenciesFilter === 'pending' && !agency.approved) ||
            (govAgenciesFilter === 'approved' && agency.approved)
          ).length === 0 ? (
            <div className="text-center py-12">
              <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No agencies found</h3>
              <p className="text-gray-600">No government agencies match the current filter.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {govAgencies
                .filter(agency =>
                  govAgenciesFilter === 'all' ||
                  (govAgenciesFilter === 'pending' && !agency.approved) ||
                  (govAgenciesFilter === 'approved' && agency.approved)
                )
                .map((agency) => (
                  <div key={agency.id} className="px-6 py-4 hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3">
                          <div>
                            <h4 className="text-sm font-medium text-gray-900">{agency.name}</h4>
                            <p className="text-sm text-gray-600">{agency.email}</p>
                            <p className="text-xs text-gray-500">{agency.agencyType} • {agency.contactPerson}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          agency.approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {agency.approved ? 'Approved' : 'Pending'}
                        </span>
                        {!agency.approved && (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleGovAgencyAction(agency.id, 'approve')}
                              disabled={govProcessingId === agency.id}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                            >
                              {govProcessingId === agency.id ? (
                                <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                              ) : (
                                <CheckCircle className="w-4 h-4 mr-1" />
                              )}
                              Approve
                            </button>
                            <button
                              onClick={() => {
                                setSelectedGovAgency(agency)
                                setShowGovRejectModal(true)
                              }}
                              disabled={govProcessingId === agency.id}
                              className="inline-flex items-center px-3 py-1 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Reject Modal */}
      {showGovRejectModal && selectedGovAgency && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Reject Government Agency</h3>
              <p className="text-sm text-gray-600 mb-4">
                Are you sure you want to reject <strong>{selectedGovAgency.name}</strong>?
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for rejection (required)
                </label>
                <textarea
                  value={govRejectReason}
                  onChange={(e) => setGovRejectReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter reason for rejection..."
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowGovRejectModal(false)
                    setGovRejectReason('')
                    setSelectedGovAgency(null)
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleGovAgencyAction(selectedGovAgency.id, 'reject')}
                  disabled={!govRejectReason.trim() || govProcessingId === selectedGovAgency.id}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {govProcessingId === selectedGovAgency.id ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-1 inline" />
                  ) : null}
                  Reject Agency
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  const tabs = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'categories', label: 'Categories', icon: Package },
    { id: 'orders', label: 'Orders', icon: TrendingUp },
    { id: 'pendingGovCerts', label: 'Pending Gov Certs', icon: Shield },
    { id: 'govAgencies', label: 'Gov Agency Approvals', icon: Shield }, // NEW TAB
  ]

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        <div className="animate-pulse space-y-4 sm:space-y-6">
          <div className="h-6 sm:h-8 bg-gray-300 rounded w-1/3 sm:w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-20 sm:h-24 bg-gray-300 rounded"></div>
            ))}
          </div>
          <div className="h-48 sm:h-64 bg-gray-300 rounded"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
        <p className="text-sm sm:text-base text-gray-600">Manage your Evoltbay marketplace</p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-2 sm:space-x-8 overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon
            // Gov Agency Approvals tab now works as a proper tab within admin dashboard
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`py-2 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'products' && <ProductsTab />}
      {activeTab === 'categories' && <CategoriesTab />}
      {activeTab === 'orders' && <OrdersTab />}
      {activeTab === 'pendingGovCerts' && <PendingGovCertificationsTab />}
      {activeTab === 'govAgencies' && <GovAgenciesTab />}
    </div>
  )
} 